import { Form, Row, Col, Input, Select } from "antd";
import { PhoneInput } from "../../../../../../components/PhoneInput";
import { FullNameFormItems } from "../../../FullNameFormItems";
import { useGetEmailFormRules } from "../../../../../../lib/hooks/getEmailFormRules";
import { IOption } from "../../../../../../types/Option";
import { useUpdateUser } from "../../../../hooks/useUpdateUser";
import { RoleSelector } from "./RoleSelector";
import { useCallback, useMemo } from "react";
import USER_ROLES from "../../../../../../types/UserRoles";

type EditProfileProps = {
  user: any;
  roles: IOption[];
  isSaveClicked: boolean;
  setIsSaveClicked: (value: boolean) => void;
  reloadData: () => void;
};

export const EditProfile = ({
  user,
  roles,
  isSaveClicked,
  setIsSaveClicked,
  reloadData,
}: EditProfileProps) => {
  const [form] = Form.useForm();

  const { rules: emailRules } = useGetEmailFormRules();

  const { isDataDisabled } = useUpdateUser(
    form,
    user,
    roles,
    isSaveClicked,
    setIsSaveClicked,
    reloadData,
  );

  const roleClassName: string = useMemo(
    () =>
      `align-center mb-4 ${user?.role === USER_ROLES.INTEGRATION_RECEPTIONIST ? "d-none" : ""}`,
    [user],
  );

  const onError = useCallback(
    () => setIsSaveClicked(false),
    [setIsSaveClicked],
  );

  const twoFactorOptions: IOption[] = useMemo(
    () => [
      { label: "Email", value: 0 },
      { label: "SMS", value: 1 },
    ],
    [],
  );

  const isTwoFactorOptionsDisabled: boolean = useMemo(() => {
    if (user?.role !== USER_ROLES.PORTAL_USER) {
      return true;
    }
    return user?.status === 3;
  }, [user]);

  return (
    <div className="mt-8 mx-6">
      <Form
        autoComplete="off"
        requiredMark={false}
        layout="vertical"
        form={form}
        onFinishFailed={onError}
        disabled={isDataDisabled}
        initialValues={user}
      >
        <FullNameFormItems />

        <Row className="align-center mb-4">
          <Col span={8}>
            <span className="text-md title">Email</span>
          </Col>
          <Col span={16}>
            <Form.Item name="email" className="mb-0" rules={emailRules}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row className="align-center mb-4">
          <Col span={8}>
            <span className="text-md title">Phone</span>
          </Col>
          <Col span={16}>
            {user.phone !== null && (
              <PhoneInput
                form={form}
                size="middle"
                formItemMargin="mb-0"
                disabled={isDataDisabled}
                label=" "
              />
            )}
          </Col>
        </Row>

        <Row className={roleClassName}>
          <Col span={8}>
            <span className="text-md title">Role</span>
          </Col>
          <RoleSelector
            user={user}
            roles={roles}
            isDataDisabled={isDataDisabled}
          />
        </Row>

        {user.role === USER_ROLES.PORTAL_USER && (
          <Row className="align-center mb-4">
            <Col span={8}>
              <span className="text-md title">2FA Sending</span>
            </Col>
            <Col span={16}>
              <Row>
                <Col span={14}>
                  <Form.Item
                    name="twoFactorAuthenticationSendingType"
                    className="mb-0"
                  >
                    <Select
                      options={twoFactorOptions}
                      disabled={isDataDisabled || isTwoFactorOptionsDisabled}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Form>
    </div>
  );
};
